import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack, Select, MenuItem, FormControl, InputLabel, Box, Button, SelectChangeEvent } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { getMonthlyReport } from '../services/financeService';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

interface StaffInfo {
  displayName: string;
  [key: string]: unknown;
}

interface PlanInfo {
  _id: string;
  name: string;
  zh_name: string;
  [key: string]: unknown;
}

interface Reservation {
  _id: string;
  staffId: StaffInfo;
  planId?: PlanInfo;
  createdType?: string;
  paymentType?: string;
  originalAmount: number;
  start: string;
  status: string;
  [key: string]: unknown;
}

interface ReportFilters {
  stylistName: string;
  plan: string;
  createdType: string;
  paymentType: string;
  status: string;
}

function MonthlyReport(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [cutoffDate, setCutoffDate] = useState<Dayjs>(dayjs());
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<number>(0);
  const [fromDate, setFromDate] = useState<Dayjs>(dayjs().subtract(1, 'month').date(dayjs().date()));

  const [filters, setFilters] = useState<ReportFilters>({
    stylistName: '',
    plan: '',
    createdType: '',
    paymentType: '',
    status: '',
  });

  const calculateMonthlyRevenue = useCallback((reservations: Reservation[]) => {
    const revenue = reservations.reduce((total, transaction) => total + transaction.originalAmount, 0);
    setMonthlyRevenue(revenue);
  }, [setMonthlyRevenue]);

  const fetchReservations = useCallback(async (date: Dayjs) => {
    const startDate = date.subtract(1, 'month').date(date.date());
    const adjustedStartDate = startDate.isValid() ? startDate : startDate.endOf('month');
    setFromDate(adjustedStartDate);

    try {
      const reportResponse = await getMonthlyReport(adjustedStartDate.toISOString(), date.toISOString());
      console.log('Fetched reservations:', reportResponse);
      const fetchedReservations = Array.isArray(reportResponse.report) ? reportResponse.report as Reservation[] : [];
      setReservations(fetchedReservations);
      calculateMonthlyRevenue(fetchedReservations);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    }
  }, [setFromDate, setReservations, calculateMonthlyRevenue]);

  useEffect(() => {
    fetchReservations(cutoffDate);
  }, [cutoffDate, fetchReservations]);

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setCutoffDate(date);
      fetchReservations(date);
    }
  };

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    console.log('Filter changed:', name, value);
    setFilters((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [name as string]: value,
      };
      // Recalculate revenue based on new filters
      const newFilteredReservations = reservations.filter((reservation) => {
        return (
          (newFilters.stylistName === '' || (reservation.staffId?.displayName?.includes(newFilters.stylistName) ?? false)) &&
          (newFilters.plan === '' || (reservation.planId?._id?.includes(newFilters.plan) ?? false)) &&
          (newFilters.createdType === '' || (reservation.createdType?.includes(newFilters.createdType) ?? false)) &&
          (newFilters.paymentType === '' || (reservation.paymentType?.includes(newFilters.paymentType) ?? false)) &&
          (newFilters.status === '' || reservation.status.includes(newFilters.status))
        );
      });
      calculateMonthlyRevenue(newFilteredReservations);
      return newFilters;
    });
  };

  const filteredReservations = reservations.filter((reservation) => {
    return (
      (filters.stylistName === '' || (reservation.staffId?.displayName?.includes(filters.stylistName) ?? false)) &&
      (filters.plan === '' || (reservation.planId?._id?.includes(filters.plan) ?? false)) &&
      (filters.createdType === '' || (reservation.createdType?.includes(filters.createdType) ?? false)) &&
      (filters.paymentType === '' || (reservation.paymentType?.includes(filters.paymentType) ?? false)) &&
      (filters.status === '' || reservation.status.includes(filters.status))
    );
  });

  const getUniqueValues = useCallback((key: string): string[] => {
    return [...new Set(reservations.map(reservation => {
      const keys = key.split('.');
      let result: unknown = reservation;
      for (const curr of keys) {
        result = result && (result as Record<string, unknown>)[curr];
      }
      return result as string;
    }))];
  }, [reservations]);

  const downloadCSV = () => {
    const reservationData: Array<Record<string, string | number>> = filteredReservations.map(reservation => ({
      'Stylist Name': reservation.staffId?.displayName ?? '',
      'Plan': language === 'zh' ? (reservation.planId?.zh_name ?? '') : (reservation.planId?.name ?? ''),
      'Created Type': reservation.createdType ?? '',
      'Payment Type': reservation.paymentType ?? '',
      'Amount': reservation.originalAmount ?? 0,
      'Date': dayjs(reservation.start).format('YYYY-MM-DD'),
      'Status': reservation.status ?? '',
    }));

    // Add a row for the monthly revenue
    reservationData.push({
      'Stylist Name': '',
      'Plan': '',
      'Created Type': '',
      'Payment Type': '',
      'Amount': `Total Revenue: $${monthlyRevenue}`,
      'Date': '',
      'Status': '',
    });

    const worksheet = XLSX.utils.json_to_sheet(reservationData);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `MonthlyReport_${cutoffDate.format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2}>
        <DatePicker
          label={t('monthlyReport.cutoffDate')}
          value={cutoffDate}
          onChange={handleDateChange}
          slotProps={{ textField: { fullWidth: true } }}
        />
        <Typography variant="body1">{t('monthlyReport.fromDate')}: {fromDate.format('YYYY-MM-DD')}</Typography>
        <Typography variant="body1">{t('monthlyReport.toDate')}: {cutoffDate.format('YYYY-MM-DD')}</Typography>

        <Box display="flex" flexDirection="row" gap={2} flexWrap="nowrap" overflow="auto">
          <FormControl style={{ minWidth: 150 }}>
            <InputLabel>{t('monthlyReport.stylistName')}</InputLabel>
            <Select
              label={t('monthlyReport.stylistName')}
              name="stylistName"
              value={filters.stylistName}
              onChange={handleFilterChange}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {getUniqueValues('staffId.displayName').map((name, index) => (
                <MenuItem key={`stylist-${index}`} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl style={{ minWidth: 150 }}>
            <InputLabel>{t('monthlyReport.plan')}</InputLabel>
            <Select
              label={t('monthlyReport.plan')}
              name="plan"
              value={filters.plan}
              onChange={handleFilterChange}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {getUniqueValues('planId._id').map((plan, index) => (
                <MenuItem key={`plan-${index}`} value={plan}>
                  {language === 'zh' ? (reservations.find(res => res.planId?._id === plan)?.planId?.zh_name ?? plan) : plan}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl style={{ minWidth: 150 }}>
            <InputLabel>{t('monthlyReport.createdType')}</InputLabel>
            <Select
              label={t('monthlyReport.createdType')}
              name="createdType"
              value={filters.createdType}
              onChange={handleFilterChange}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {getUniqueValues('createdType').map((type, index) => (
                <MenuItem key={`createdType-${index}`} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl style={{ minWidth: 150 }}>
            <InputLabel>{t('monthlyReport.paymentType')}</InputLabel>
            <Select
              label={t('monthlyReport.paymentType')}
              name="paymentType"
              value={filters.paymentType}
              onChange={handleFilterChange}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {getUniqueValues('paymentType').map((type, index) => (
                <MenuItem key={`paymentType-${index}`} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl style={{ minWidth: 150 }}>
            <InputLabel>{t('monthlyReport.status')}</InputLabel>
            <Select
              label={t('monthlyReport.status')}
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {getUniqueValues('status').map((status, index) => (
                <MenuItem key={`status-${index}`} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('reservationList.stylist')}</TableCell>
                <TableCell>{t('reservationList.plan')}</TableCell>
                <TableCell>{t('reservationList.createdType')}</TableCell>
                <TableCell>{t('reservationList.paymentType')}</TableCell>
                <TableCell>{t('reservationList.originalAmount')}</TableCell>
                <TableCell>{t('reservationList.date')}</TableCell>
                <TableCell>{t('reservationList.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReservations.map(reservation => (
                <TableRow key={reservation._id}>
                  <TableCell>{reservation.staffId?.displayName ?? ''}</TableCell>
                  <TableCell>
                    {language === 'zh' ? (reservation.planId?.zh_name ?? '') : (reservation.planId?.name ?? '')}
                  </TableCell>
                  <TableCell>{reservation.createdType ?? ''}</TableCell>
                  <TableCell>{reservation.paymentType ?? ''}</TableCell>
                  <TableCell>${reservation.originalAmount}</TableCell>
                  <TableCell>{dayjs(reservation.start).format('YYYY-MM-DD')}</TableCell>
                  <TableCell>{reservation.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6">{t('monthlyReport.monthlyRevenue')}: ${monthlyRevenue}</Typography>

        <Button variant="contained" color="primary" onClick={downloadCSV}>
          {t('monthlyReport.downloadCSV')}
        </Button>
      </Stack>
    </LocalizationProvider>
  );
}

export default MonthlyReport;
